import React, { useMemo } from 'react';
import { FormHelperText } from '@material-ui/core';
import TextField, { TextFieldProps } from '@material-ui/core/TextField';
import { ControllerProps, FieldValues, useController, useFormContext } from 'react-hook-form';

import { InputAdornment } from './InputAdornment';

import { useStyles } from './styles';

export type QuantityInputFieldProps = Omit<ControllerProps<FieldValues>, 'as' | 'render' | 'name'> &
  TextFieldProps & {
    max: number;
    min: number;
  };

export const QuantityInputField = ({
  name = 'quantity',
  defaultValue,
  rules,
  max,
  min,
  disabled,
  label,
  control,
  InputProps,
  ...props
}: QuantityInputFieldProps) => {
  const classes = useStyles();

  const { control: formControl, setValue } = useFormContext() ?? {};

  const {
    field: { value },
    fieldState: { error },
  } = useController({
    name: name,
    defaultValue,
    rules,
    control: control ?? formControl,
  });

  const isIncremntBtnDisabled = useMemo(() => (disabled ? true : value + 1 > max), [disabled, value, max]);

  const isDecremntBtnDisabled = useMemo(() => (disabled ? true : value - 1 < min), [disabled, value, min]);

  const handleIncrementBtnClick = () => !disabled && setValue(name, value + 1);

  const handleDecrementBtnClick = () => !disabled && setValue(name, value - 1);

  return (
    <>
      {label && (
        <label htmlFor={name} className={classes.label}>
          {label}
        </label>
      )}
      <TextField
        {...props}
        value={value}
        type="number"
        variant="standard"
        disabled={disabled}
        onChange={e => setValue(name, e?.target?.value)}
        classes={{ root: classes.textFieldRoot }}
        InputProps={{
          ...InputProps,
          classes: { root: classes.inputRoot, input: classes.input, underline: classes.underline },
          endAdornment: (
            <InputAdornment
              isDecremntBtnDisabled={isDecremntBtnDisabled}
              isIncremntBtnDisabled={isIncremntBtnDisabled}
              onIncrementBtnClick={handleIncrementBtnClick}
              onDecrementBtnClick={handleDecrementBtnClick}
            />
          ),
        }}
      />
      {error && <FormHelperText error={true}>{error?.message}</FormHelperText>}
    </>
  );
};
